import { useEffect, useMemo, useRef, useState } from "react";
import { toPng } from "html-to-image";
import { X, Share2, Download, Copy, Sparkles, Heart, Gift, Calendar, MapPin } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import GuestClaimPrompt from "@/components/GuestClaimPrompt";
import { useAuth } from "@/context/AuthContext";

// --- A/B test variants for the share modal copy ---
// Stable per-browser assignment via localStorage. Tracked via backend POST /api/ab/share.
const COPY_VARIANTS = {
  A: {
    ticket: {
      banner: "Réservation confirmée !",
      headline: "Invitez vos amis à vous rejoindre 🎉",
      sub: "Partagez l'événement sur vos réseaux : c'est gratuit et ça aide vraiment l'organisateur.",
      badge: "J'y serai",
      defaultText: (title, dateStr, location) =>
        `J'y serai ! 🎉 Rendez-vous à « ${title} »${dateStr ? ` le ${dateStr}` : ""}${location ? ` à ${location}` : ""}. Réservez votre place sur Zevents.`,
    },
    donation: {
      banner: "Merci pour votre don !",
      headline: "Faites passer le mot 💛",
      sub: "Plus on est nombreux, plus l'impact est grand. Partagez en un clic.",
      badge: "Je soutiens",
      defaultText: (title) => `Je soutiens « ${title} » sur Zevents ! 💚 Rejoignez-moi pour faire la différence.`,
    },
    gift: {
      banner: "Liste de cadeaux prête !",
      headline: "Diffusez votre liste à vos proches 🎁",
      sub: "Plus la liste circule, plus vous aurez de belles surprises. Un clic suffit.",
      badge: "Liste de cadeaux",
      defaultText: (title) => `Voici notre liste de cadeaux « ${title} » sur Zevents 🎁 — réservez ou contribuez en un clic !`,
    },
  },
  B: {
    ticket: {
      banner: "C'est officiel : vous êtes inscrit·e !",
      headline: "Rejoignez-moi à cet événement 🚀",
      sub: "Un clic suffit pour inviter vos proches. Ensemble, c'est toujours mieux.",
      badge: "Rejoignez-moi",
      defaultText: (title, dateStr, location) =>
        `Rejoignez-moi à « ${title} »${dateStr ? ` le ${dateStr}` : ""}${location ? ` (${location})` : ""} ! 🚀 Réservez votre place sur Zevents.`,
    },
    donation: {
      banner: "Don bien reçu, merci 💛",
      headline: "Et si on multipliait l'impact ?",
      sub: "Invitez 3 amis : leur participation peut tout changer.",
      badge: "On y va ensemble",
      defaultText: (title) => `Et si on soutenait « ${title} » ensemble ? 💛 Je viens de faire un don sur Zevents — rejoignez-moi !`,
    },
    gift: {
      banner: "Votre liste est en ligne 🎁",
      headline: "Partagez et laissez la magie opérer",
      sub: "Vos proches verront en un clic ce qui vous ferait plaisir. C'est tellement plus simple !",
      badge: "Notre liste",
      defaultText: (title) => `Découvrez notre liste de cadeaux « ${title} » 🎁 — un seul clic pour réserver ou contribuer.`,
    },
  },
};

function getOrAssignVariant() {
  try {
    const stored = localStorage.getItem("zev_ab_share_copy");
    if (stored === "A" || stored === "B") return stored;
  } catch (_e) { /* localStorage unavailable */ }
  const v = Math.random() < 0.5 ? "A" : "B";
  try { localStorage.setItem("zev_ab_share_copy", v); } catch (_e) { /* ignore */ }
  return v;
}

function getSessionId() {
  try {
    let s = localStorage.getItem("zev_ab_session");
    if (!s) {
      s = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
      localStorage.setItem("zev_ab_session", s);
    }
    return s;
  } catch (_e) { return null; }
}

function track(variant, kind, action, entityId) {
  // Fire-and-forget; don't block UI.
  try {
    api.post("/ab/share", {
      variant, kind, action,
      entity_id: entityId || null,
      session_id: getSessionId(),
    }).catch(() => { /* swallow */ });
  } catch (_e) { /* ignore */ }
}

/**
 * Celebration + share modal shown after a buyer:
 *  - buys a ticket (kind="ticket")
 *  - makes a donation (kind="donation")
 *
 * Generates a branded shareable PNG card via html-to-image, and exposes
 * native share / WhatsApp / Facebook / X / copy-link actions.
 *
 * A/B test: 2 copy variants are assigned per browser (50/50). Tracked via /api/ab/share.
 */
export default function ShareCelebrationModal({
  open,
  onClose,
  kind = "ticket",        // "ticket" | "donation"
  entityId,               // event id or fundraiser id (for tracking + share URL)
  title,                  // event title or fundraiser title
  subtitle,               // organizer / cause line
  dateStr,                // formatted date (events only)
  location,               // location (events only)
  amount,                 // donation amount (donations only)
  currency = "€",
  imageUrl,               // optional cover image (event/fundraiser)
  shareUrl,               // OPTIONAL — defaults to backend OG endpoint (crawler-friendly)
  primaryCtaLabel = "Voir mon billet",
  onPrimaryCta,           // callback when user clicks the primary cta
  guestEmail,             // OPTIONAL — when provided AND user not logged in, shows GuestClaimPrompt
  guestName,
}) {
  const cardRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const variant = useMemo(() => getOrAssignVariant(), []);
  const { user } = useAuth();

  const copy = (COPY_VARIANTS[variant] || COPY_VARIANTS.A)[kind] || (COPY_VARIANTS.A)[kind];

  // Build a crawler-friendly share URL pointing to the backend OG endpoint.
  // Facebook / X / WhatsApp scrape this URL → see OG meta → humans get redirected to the SPA.
  const computedShareUrl = useMemo(() => {
    if (shareUrl) return shareUrl;
    const backend = process.env.REACT_APP_BACKEND_URL;
    if (!backend || !entityId) return typeof window !== "undefined" ? window.location.href : "";
    const path = kind === "donation" ? "fundraiser" : kind === "gift" ? "gift-list" : "event";
    return `${backend.replace(/\/$/, "")}/api/og/${path}/${entityId}`;
  }, [shareUrl, entityId, kind]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("keydown", onKey);
    // Track impression once per open
    track(variant, kind, "impression", entityId);
    return () => document.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  const isDonation = kind === "donation";
  const isGift = kind === "gift";
  const shareText = isDonation
    ? copy.defaultText(title)
    : isGift
      ? copy.defaultText(title)
      : copy.defaultText(title, dateStr, location);

  const renderPng = async () => {
    if (!cardRef.current) return null;
    return toPng(cardRef.current, {
      cacheBust: true,
      pixelRatio: 2,
      backgroundColor: isDonation ? "#DA7101" : "#01696F",
      skipFonts: true,
    });
  };

  const nativeShare = async () => {
    track(variant, kind, "share_native", entityId);
    setBusy(true);
    try {
      const dataUrl = await renderPng();
      const blob = dataUrl ? await (await fetch(dataUrl)).blob() : null;
      const file = blob ? new File([blob], `zevents-${kind}.png`, { type: "image/png" }) : null;
      if (file && navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title, text: shareText });
      } else if (navigator.share) {
        await navigator.share({ title, text: shareText, url: computedShareUrl });
      } else if (dataUrl) {
        downloadDataUrl(dataUrl, `zevents-${kind}.png`);
        toast.success("Image téléchargée — partagez-la où vous voulez !");
      }
    } catch (e) {
      if (e?.name !== "AbortError") toast.error("Partage indisponible");
    } finally {
      setBusy(false);
    }
  };

  const downloadDataUrl = (dataUrl, filename) => {
    const a = document.createElement("a");
    a.href = dataUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const downloadImage = async () => {
    track(variant, kind, "download_image", entityId);
    setBusy(true);
    try {
      const dataUrl = await renderPng();
      if (dataUrl) downloadDataUrl(dataUrl, `zevents-${kind}.png`);
    } catch (e) {
      toast.error("Téléchargement impossible");
    } finally {
      setBusy(false);
    }
  };

  const copyLink = async () => {
    track(variant, kind, "copy_link", entityId);
    try {
      await navigator.clipboard.writeText(`${shareText}\n${computedShareUrl}`);
      toast.success("Lien copié dans le presse-papiers");
    } catch {
      toast.error("Copie impossible");
    }
  };

  const openShare = (kindOf) => {
    track(variant, kind, `share_${kindOf}`, entityId);
    const url = encodeURIComponent(computedShareUrl);
    const text = encodeURIComponent(shareText);
    let target = "";
    if (kindOf === "whatsapp") target = `https://wa.me/?text=${text}%20${url}`;
    else if (kindOf === "facebook") target = `https://www.facebook.com/sharer/sharer.php?u=${url}&quote=${text}`;
    else if (kindOf === "x") target = `https://twitter.com/intent/tweet?text=${text}&url=${url}`;
    if (target) window.open(target, "_blank", "noopener,noreferrer,width=600,height=600");
  };

  const handlePrimary = () => {
    track(variant, kind, "primary_cta", entityId);
    onPrimaryCta?.();
  };

  const handleClose = () => {
    track(variant, kind, "close", entityId);
    onClose?.();
  };

  const accent = isDonation ? "#DA7101" : "#01696F";
  const accentSoft = isDonation ? "#FCE2C3" : "#D6EBEC";

  return (
    <div
      onClick={handleClose}
      data-testid="share-celebration-overlay"
      data-ab-variant={variant}
      style={{
        position: "fixed", inset: 0, background: "rgba(15,20,28,0.62)",
        display: "flex", alignItems: "center", justifyContent: "center",
        zIndex: 9999, padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        data-testid="share-celebration-modal"
        data-ab-variant={variant}
        style={{
          background: "white", borderRadius: 18, width: "100%", maxWidth: 460,
          maxHeight: "92vh", overflow: "auto", boxShadow: "0 22px 60px rgba(0,0,0,0.32)",
          position: "relative", animation: "z-pop .25s ease-out",
        }}
      >
        <button
          onClick={handleClose}
          data-testid="share-celebration-close"
          aria-label="Fermer"
          style={{
            position: "absolute", top: 12, right: 12, zIndex: 2,
            background: "rgba(255,255,255,0.9)", border: "none", borderRadius: 999,
            width: 32, height: 32, display: "grid", placeItems: "center", cursor: "pointer",
          }}
        >
          <X size={18} />
        </button>

        {/* Banner */}
        <div style={{
          background: `linear-gradient(135deg, ${accent} 0%, ${accent}cc 100%)`,
          color: "white", padding: "26px 22px 18px", textAlign: "center",
        }}>
          <div style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            background: "rgba(255,255,255,0.18)", padding: "6px 12px",
            borderRadius: 999, fontSize: 12, fontWeight: 600, marginBottom: 10,
          }} data-testid="share-celebration-banner">
            <Sparkles size={14} /> {copy.banner}
          </div>
          <h2 style={{ fontSize: 22, fontWeight: 800, margin: 0, lineHeight: 1.25 }} data-testid="share-celebration-headline">
            {copy.headline}
          </h2>
          <p style={{ fontSize: 13, opacity: 0.92, margin: "8px 0 0" }} data-testid="share-celebration-sub">
            {copy.sub}
          </p>
        </div>

        {/* Preview card (also used to generate the PNG) */}
        <div style={{ padding: "18px 18px 0" }}>
          <div
            ref={cardRef}
            data-testid="share-celebration-card"
            style={{
              borderRadius: 16, overflow: "hidden", border: `1px solid ${accentSoft}`,
              background: "white",
            }}
          >
            <div style={{ position: "relative" }}>
              {imageUrl ? (
                <img src={imageUrl} alt="" crossOrigin="anonymous"
                  style={{ width: "100%", height: 150, objectFit: "cover", display: "block" }} />
              ) : (
                <div style={{
                  height: 150, background: `linear-gradient(135deg, ${accent}, ${accent}99)`,
                  display: "grid", placeItems: "center", color: "white",
                }}>
                  {isDonation
                    ? <Heart size={56} fill="white" strokeWidth={1.4} />
                    : isGift
                      ? <Gift size={56} strokeWidth={1.4} />
                      : <Sparkles size={56} strokeWidth={1.4} />}
                </div>
              )}
              <div style={{
                position: "absolute", top: 10, left: 10,
                background: "white", color: accent, padding: "4px 10px",
                borderRadius: 999, fontSize: 11, fontWeight: 800, letterSpacing: 0.4,
                textTransform: "uppercase",
              }}>
                {copy.badge}
              </div>
            </div>
            <div style={{ padding: "14px 16px 16px", background: "white" }}>
              <div style={{ fontWeight: 800, fontSize: 16, color: "#0F1418", lineHeight: 1.3 }}>
                {title}
              </div>
              {subtitle && (
                <div style={{ fontSize: 12, color: "#5B6671", marginTop: 3 }}>{subtitle}</div>
              )}
              {!isDonation && !isGift && (dateStr || location) && (
                <div style={{ display: "grid", gap: 6, marginTop: 12 }}>
                  {dateStr && (
                    <div style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 12, color: "#0F1418" }}>
                      <Calendar size={14} color={accent} /> {dateStr}
                    </div>
                  )}
                  {location && (
                    <div style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 12, color: "#0F1418" }}>
                      <MapPin size={14} color={accent} /> {location}
                    </div>
                  )}
                </div>
              )}
              {isDonation && amount != null && (
                <div style={{ marginTop: 10, fontSize: 13, color: "#0F1418" }}>
                  Mon don : <strong style={{ color: accent }}>{Number(amount).toFixed(0)} {currency}</strong>
                </div>
              )}
              <div style={{
                marginTop: 14, paddingTop: 12, borderTop: `1px dashed ${accentSoft}`,
                display: "flex", justifyContent: "space-between", alignItems: "flex-end",
              }}>
                <div>
                  <div style={{ fontSize: 18, fontWeight: 800, color: "#0F1418", lineHeight: 1, letterSpacing: -0.3 }}>
                    <span style={{ color: "#DA7101" }}>z</span>events
                  </div>
                  <div style={{ fontSize: 9, color: "#5B6671", fontWeight: 600, letterSpacing: 1.2, marginTop: 2 }}>
                    by ALO FINANCES
                  </div>
                </div>
                <span style={{ fontSize: 11, color: accent, fontWeight: 700 }}>#Zevents</span>
              </div>
            </div>
          </div>
        </div>

        {/* Share actions */}
        <div style={{ padding: "16px 18px 6px" }}>
          <button
            onClick={nativeShare}
            disabled={busy}
            data-testid="share-celebration-native"
            className="z-btn z-btn-primary"
            style={{ width: "100%", justifyContent: "center" }}
          >
            <Share2 size={16} /> {busy ? "Préparation…" : "Partager maintenant"}
          </button>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8, marginTop: 10 }}>
            <button
              onClick={() => openShare("whatsapp")}
              data-testid="share-celebration-whatsapp"
              className="z-btn z-btn-outline"
              style={{ justifyContent: "center", padding: "10px 6px", fontSize: 13 }}
            >
              WhatsApp
            </button>
            <button
              onClick={() => openShare("facebook")}
              data-testid="share-celebration-facebook"
              className="z-btn z-btn-outline"
              style={{ justifyContent: "center", padding: "10px 6px", fontSize: 13 }}
            >
              Facebook
            </button>
            <button
              onClick={() => openShare("x")}
              data-testid="share-celebration-x"
              className="z-btn z-btn-outline"
              style={{ justifyContent: "center", padding: "10px 6px", fontSize: 13 }}
            >
              X
            </button>
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
            <button
              onClick={copyLink}
              data-testid="share-celebration-copy"
              className="z-btn z-btn-ghost"
              style={{ flex: 1, justifyContent: "center", fontSize: 13 }}
            >
              <Copy size={14} /> Copier le lien
            </button>
            <button
              onClick={downloadImage}
              disabled={busy}
              data-testid="share-celebration-download"
              className="z-btn z-btn-ghost"
              style={{ flex: 1, justifyContent: "center", fontSize: 13 }}
            >
              <Download size={14} /> Image
            </button>
          </div>
          {!user && guestEmail && (
            <GuestClaimPrompt
              email={guestEmail}
              name={guestName || ""}
              next={kind === "ticket" ? "/my-tickets" : "/engagements"}
              context={kind === "ticket" ? "votre billet" : kind === "donation" ? "votre don" : "votre cadeau"}
            />
          )}
        </div>

        {/* Footer CTA */}
        <div style={{ padding: "10px 18px 20px" }}>
          {onPrimaryCta ? (
            <button
              onClick={handlePrimary}
              data-testid="share-celebration-primary-cta"
              className="z-btn z-btn-outline"
              style={{ width: "100%", justifyContent: "center" }}
            >
              {primaryCtaLabel}
            </button>
          ) : (
            <button
              onClick={handleClose}
              data-testid="share-celebration-skip"
              className="z-btn z-btn-ghost"
              style={{ width: "100%", justifyContent: "center", fontSize: 13 }}
            >
              Plus tard
            </button>
          )}
        </div>
      </div>

      <style>{`
        @keyframes z-pop {
          from { transform: translateY(12px) scale(.96); opacity: 0; }
          to   { transform: translateY(0)    scale(1);    opacity: 1; }
        }
      `}</style>
    </div>
  );
}
